import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { getEmployeeLeavesDataListForAdmin, updateLeaveApplicationReview, testBackendConnectivity, mapLeaveStatusToString, formatDateForApi, formatDateString } from '../../services/leaveService';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const LeaveApplicationManagementScreen = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [reviewData, setReviewData] = useState({
    status: 'Accepted',
    reason: ''
  });
  const [filterStatus, setFilterStatus] = useState('All');
  const navigate = useNavigate();
  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('=== ADMIN FETCH START ===');
      console.log('Starting admin leave applications fetch...');
      
      // First test backend connectivity
      console.log('🔍 Testing backend connectivity...');
      const connectivityTest = await testBackendConnectivity();
      console.log('Backend connectivity test result:', connectivityTest);
      
      if (!connectivityTest.success) {
        throw new Error(`Backend connectivity failed: ${connectivityTest.message}`);
      }
      
      const adminDetails = {
        companyCode: localStorage.getItem('companyCode'),
        adminCode: localStorage.getItem('employeeCode'),
        token: localStorage.getItem('token'),
        sessionToken: localStorage.getItem('sessionToken')
      };

      console.log('Admin details check:', {
        companyCode: adminDetails.companyCode ? 'Present' : 'Missing',
        adminCode: adminDetails.adminCode ? 'Present' : 'Missing', 
        token: adminDetails.token ? 'Present' : 'Missing',
        sessionToken: adminDetails.sessionToken ? 'Present' : 'Missing'
      });

      // Validate admin details
      if (!adminDetails.companyCode || !adminDetails.adminCode || !adminDetails.token || !adminDetails.sessionToken) {
        throw new Error('Missing admin details. Please login again.');
      }

      console.log('📡 Calling getEmployeeLeavesDataListForAdmin...');
      const applications = await getEmployeeLeavesDataListForAdmin(adminDetails.companyCode);
      console.log('📥 Admin API Response received:', applications);
      console.log('Response type:', typeof applications);
      console.log('Is array:', Array.isArray(applications));
      console.log('Length:', applications?.length || 'N/A');

      if (!applications) {
        console.error('No applications data received');
        setError('No data received from server');
        setApplications([]);
        return;
      }

      if (Array.isArray(applications) && applications.length > 0) {
        // Sort applications by date (newest first)
        const sortedApplications = applications
          .filter(app => app && typeof app === 'object') // Filter out invalid entries
          .sort((a, b) => {
            const dateA = new Date(a.AppDate || 0);
            const dateB = new Date(b.AppDate || 0);
            return dateB - dateA;
          })
          .map(app => ({
            ...app,
            LeaveStatus: mapLeaveStatusToString(app.LeaveStatus)
          }));
        
        console.log('✅ Successfully loaded', sortedApplications.length, 'applications');
        console.log('First application:', sortedApplications[0]);
        setApplications(sortedApplications);
      } else {
        console.log('⚠️ No applications found or invalid format');
        setError('No leave applications found');
        setApplications([]);
      }
    } catch (error) {
      console.error('=== ADMIN FETCH ERROR ===');
      console.error('Error fetching applications:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setError(error.message || 'Failed to fetch leave applications');
      setApplications([]);
    } finally {
      setLoading(false);
      console.log('=== ADMIN FETCH END ===');
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleReview = (application) => {
    setSelectedApplication(application);
    setReviewData({
      status: 'Accepted',
      reason: ''
    });
    setShowReviewModal(true);
  };

  const handleReviewSubmit = async () => {
    try {
      if (!selectedApplication) return;

      // Get admin details from localStorage
      const adminName = localStorage.getItem('userName');
      const adminCode = localStorage.getItem('employeeCode');
      const token = localStorage.getItem('token');
      const sessionToken = localStorage.getItem('sessionToken');
      
      if (!adminCode || !adminName || !token || !sessionToken) {
        throw new Error('Admin details not found. Please login again.');
      }
      
      const currentReviewDate = new Date();
      const formattedReviewDateForApi = currentReviewDate.toISOString().split('T')[0];
      const formattedReviewDateStr = formatDateString(currentReviewDate.toISOString());

      const reviewSubmissionData = {
        ...selectedApplication,
        status: reviewData.status,
        reviewReason: reviewData.reason,
        reviewedBy: adminName,
        reviewDate: formattedReviewDateForApi,
        reviewDateStr: formattedReviewDateStr,
        // Ensure all dates are formatted for the API consistently
        AppDate: formatDateForApi(selectedApplication.AppDate), // Apply formatting
        AppDateStr: formatDateString(selectedApplication.AppDate), // Ensure string format is also updated if needed for display
        Designation: selectedApplication.Designation,
        EMPAUTOID: selectedApplication.EMPAUTOID,
        EMPCODE: selectedApplication.EMPCODE,
        EmployeeName: selectedApplication.EmployeeName,
        EmployeeType: selectedApplication.EmployeeType,
        LVAPPID: selectedApplication.LVAPPID,
        LVDateF: formatDateForApi(selectedApplication.LVDateF), // Apply formatting
        LVDateFStr: formatDateString(selectedApplication.LVDateF), // Ensure string format is also updated if needed for display
        LVDateT: formatDateForApi(selectedApplication.LVDateT), // Apply formatting
        LVDateTStr: formatDateString(selectedApplication.LVDateT), // Ensure string format is also updated if needed for display
        LVDays: selectedApplication.LVDays,
        LVReason: selectedApplication.LVReason,
        LVRemark: selectedApplication.LVRemark,
        LeavesType: selectedApplication.LeavesType,
        ORGAUTOID: selectedApplication.ORGAUTOID,
      };

      console.log('Review Submission Data (before API call):', reviewSubmissionData);
      console.log('Original selectedApplication:', selectedApplication);
      console.log('Formatted AppDate:', formatDateForApi(selectedApplication.AppDate));
      console.log('Formatted LVDateF:', formatDateForApi(selectedApplication.LVDateF));
      console.log('Formatted LVDateT:', formatDateForApi(selectedApplication.LVDateT));

      await updateLeaveApplicationReview(reviewSubmissionData);

      toast.success('Leave application reviewed successfully');
      setShowReviewModal(false);
      fetchApplications(); // Refresh the list
    } catch (error) {
      console.error('Error reviewing application:', error);
      if (error.message.includes('login again')) {
        // Clear localStorage and redirect to login
        localStorage.clear();
        navigate('/login');
      }
      toast.error(error.message || 'Error reviewing leave application');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Pending': 'warning',
      'Accepted': 'success',
      'Rejected': 'danger',
      'Hold': 'info'
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status || 'Pending'}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return format(new Date(dateStr), 'dd-MM-yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr;
    }
  };

  const filteredApplications = filterStatus === 'All' 
    ? applications 
    : applications.filter(app => app.LeaveStatus === filterStatus);

  const ReviewModal = () => (
    <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Review Leave Application</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedApplication && (
          <Form>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Employee Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedApplication.EmployeeName || ''}
                    readOnly
                    className="form-control-hover"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Leave Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedApplication.LeavesType || ''}
                    readOnly
                    className="form-control-hover"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>From Date</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatDate(selectedApplication.LVDateF)}
                    readOnly
                    className="form-control-hover"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>To Date</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatDate(selectedApplication.LVDateT)}
                    readOnly
                    className="form-control-hover"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Reason</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedApplication.LVReason || ''}
                    readOnly
                    className="form-control-hover"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={reviewData.status}
                    onChange={(e) => setReviewData(prev => ({ ...prev, status: e.target.value }))}
                    className="form-control-hover"
                  >
                    <option value="Accepted">Accept</option>
                    <option value="Rejected">Reject</option>
                    <option value="Hold">Hold</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Review Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    value={reviewData.reason}
                    onChange={(e) => setReviewData(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                    className="form-control-hover"
                    placeholder="Enter your review comments"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowReviewModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleReviewSubmit}>
          Submit Review
        </Button>
      </Modal.Footer>
    </Modal>
  );

  if (loading) {
    return (
      <Container className="mt-4">
        <Alert variant="info">Loading leave applications...</Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }
  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>Leave Applications</h2>
        </Col>
        <Col xs="auto">
          <Form.Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-auto"
          >
            <option value="All">All Applications</option>
            <option value="Pending">Pending</option>
            <option value="Accepted">Accepted</option>
            <option value="Rejected">Rejected</option>
            <option value="Hold">Hold</option>
          </Form.Select>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Leave Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Days</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Applied On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map((application) => (
                    <tr key={`leave-app-${application.LVAPPID}`}>
                      <td>{application.EmployeeName}</td>
                      <td>{application.Department}</td>
                      <td>{application.LeavesType}</td>
                      <td>{formatDate(application.LVDateF)}</td>
                      <td>{formatDate(application.LVDateT)}</td>
                      <td>{application.LVDays}</td>
                      <td>{application.LVReason}</td>
                      <td>{getStatusBadge(application.LeaveStatus)}</td>
                      <td>{formatDate(application.AppDate)}</td>
                      <td>
                        {application.LeaveStatus === 'Pending' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleReview(application)}
                          >
                            Review
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <ReviewModal />
    </Container>
  );
};

export default LeaveApplicationManagementScreen; 